const initialized = new WeakSet();

function initializeStudio(container) {
  const studios = container.matches?.('[data-elvyqo-studio]')
    ? [container]
    : container.querySelectorAll?.('[data-elvyqo-studio]');

  if (!studios?.length || !('IntersectionObserver' in window)) return;

  studios.forEach((studio) => {
    if (initialized.has(studio)) return;
    initialized.add(studio);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    studio.classList.add('elvyqo-studio--enhanced');

    if (studio.matches('.elvyqo-homepage')) initializeHomepageParallax(studio);

    const revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -4% 0px' },
    );

    studio.querySelectorAll('[data-reveal]').forEach((element) => revealObserver.observe(element));
  });
}

function initializeHomepageParallax(homepage) {
  const targets = [
    {
      element: homepage.querySelector('.elvyqo-homepage__hero'),
      property: '--elvyqo-hero-parallax',
      desktopOffset: 16,
      mobileOffset: 8,
    },
    {
      element: homepage.querySelector('.elvyqo-homepage__about'),
      property: '--elvyqo-about-parallax',
      desktopOffset: 8,
      mobileOffset: 4,
    },
  ].filter((target) => target.element);
  if (!targets.length) return;

  const visibleTargets = new Set();
  let frameRequested = false;
  const mobileViewport = window.matchMedia('(max-width: 749px)');

  const updateParallax = () => {
    frameRequested = false;
    visibleTargets.forEach((target) => {
      const bounds = target.element.getBoundingClientRect();
      const progress = (window.innerHeight - bounds.top) / (window.innerHeight + bounds.height);
      const maximumOffset = mobileViewport.matches ? target.mobileOffset : target.desktopOffset;
      const offset = (Math.min(1, Math.max(0, progress)) - 0.5) * maximumOffset * 2;

      homepage.style.setProperty(target.property, `${offset.toFixed(1)}px`);
    });
  };

  const scheduleParallaxUpdate = () => {
    if (frameRequested || !visibleTargets.size) return;
    frameRequested = true;
    window.requestAnimationFrame(updateParallax);
  };

  const imageObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const target = targets.find((item) => item.element === entry.target);
      if (!target) return;

      if (entry.isIntersecting) {
        visibleTargets.add(target);
        scheduleParallaxUpdate();
      } else {
        visibleTargets.delete(target);
        homepage.style.setProperty(target.property, '0px');
      }
    });
  });

  targets.forEach(({ element }) => imageObserver.observe(element));
  window.addEventListener('scroll', scheduleParallaxUpdate, { passive: true });
  window.addEventListener('resize', scheduleParallaxUpdate, { passive: true });

  const pageWrapper = document.querySelector('.page-wrapper');
  pageWrapper?.addEventListener('scroll', scheduleParallaxUpdate, { passive: true });

  const homepageSection = homepage.closest('.shopify-section') || homepage;
  const handleSectionUnload = (event) => {
    if (event.target !== homepageSection && !event.target?.contains?.(homepage)) return;

    imageObserver.disconnect();
    window.removeEventListener('scroll', scheduleParallaxUpdate);
    window.removeEventListener('resize', scheduleParallaxUpdate);
    pageWrapper?.removeEventListener('scroll', scheduleParallaxUpdate);
    document.removeEventListener('shopify:section:unload', handleSectionUnload);
  };

  document.addEventListener('shopify:section:unload', handleSectionUnload);
}

function playIntro() {
  const intro = document.querySelector('[data-elvyqo-intro]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!intro || reducedMotion || window.Shopify?.designMode) return;

  requestAnimationFrame(() => intro.classList.add('is-active'));
  window.setTimeout(() => {
    intro.classList.remove('is-active');
    intro.classList.add('is-leaving');
    window.setTimeout(() => intro.remove(), 450);
  }, 950);
}

playIntro();
initializeStudio(document);
document.addEventListener('shopify:section:load', (event) => initializeStudio(event.target));